import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Button } from 'reactstrap';
import { DatePicker } from 'components/common';
import { formatDate, formatDateTime } from 'services/util';
import styles from "assets/scss/task_management.module.css";
import { FaTimes } from 'react-icons/fa';
import { LocalForm } from 'react-redux-form';
import InputFile from 'components/common/InputFile';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { Multiselect } from "multiselect-react-dropdown";

function TeamProgressModal({ task, onClose, onUpdate, availableEmployees, fetchLabels, groups, buckets, userPermission }) {
    const [assignedEmployees, setAssignedEmployees] = useState(task?.employee || []);
    const [selectedLabels, setSelectedLabels] = useState(task?.task_label || []);
    const [selectedGroup, setSelectedGroup] = useState(task?.employees_group || '');
    const [isLabelChange, setEditLabel] = useState(false);

    useEffect(() => {
        setAssignedEmployees(task?.employee);
        setSelectedLabels(defaultOptions);
        setSelectedGroup(task?.employees_group);
    }, [task, fetchLabels]);

    const handleLabelSelect = (selectedOptions) => {
        setSelectedLabels(selectedOptions.value);
    };

    const handleRemove = (selectedOptions) => {
        setSelectedLabels(selectedOptions.value);
    };

    const groupOptions = groups?.map(group => ({
        value: group.id,
        label: group.group_name
    }));

    const labelOptions = fetchLabels?.map(label => ({
        value: label.id,
        label: label.label_name,
        color_code: label.color_code,
    }));

    const defaultOptions = task?.task_label?.map(labelId => {
        const label = labelOptions?.find(label => label?.value === labelId?.id);
        return { value: labelId, label: label?.label, color_code: label?.color_code || '' };
    });

    const defaultBucketOption = task?.planner_bucket;
    const bucket = buckets.find(bucket => bucket.id === defaultBucketOption);

    const bucketName = bucket ? bucket.bucket_name : '';



    const selectedGroupObject = groups?.find(group => group.id === selectedGroup);
    const multiSelectStyle = {
        chips: { background: '#72B566' },
    };

    return (
        <Modal isOpen={true} toggle={onClose} className={styles.update_task_modal} style={{ maxWidth: '800px', minWidth: '300px' }}>
            <ModalHeader toggle={onClose}>
                <div className={styles.modal_header_content}>
                    <span className={styles.task_title}><strong><Trans>Task Details</Trans></strong></span>
                </div>
            </ModalHeader>
            <ModalBody style={{ margin: '0 30px' }}>
                <div className={styles.form_group}>
                    <div className={styles.nonEditableField}>{task?.bucket_task_name}</div>
                </div>
                <div className={styles.form_group}>
                    <label><strong><Trans>Last Changed</Trans></strong>: {formatDateTime(task.updated_at)}</label>
                </div>
                {userPermission && (
                    <div className={styles.form_group}>
                        <label><strong><Trans>Assigned Employees</Trans></strong></label>
                        <div className="d-flex">
                            {assignedEmployees.map(employeeId => {
                                const employee = availableEmployees.find(emp => emp.id === employeeId);
                                return (
                                    <div key={employeeId} className={styles.assignedEmployee}>
                                        {employee?.full_name}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {!isLabelChange ?  
                  <div className='d-flex justify-content-start align-items-center'>
                    <div className={styles.form_group}>
                    <label><strong><Trans>Selected Labels</Trans></strong></label>
                    <div className={styles.selectedLabels}>
                        {defaultOptions?.map((labelOption) => (
                            <span 
                                key={labelOption.value} 
                                style={{
                                    borderRadius: '10%', 
                                    backgroundColor: labelOption.color_code || '',
                                    marginRight: "3px",
                                }} 
                                className='p-2 text-white'>
                                {labelOption.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div className='mx-auto'/>
                </div>
                :
                <div className={styles.form_group}>
                    <label><strong><Trans>Task Label</Trans></strong></label>
                    <div className={styles.groupSelectContainer}>
                    <Multiselect
                        options={labelOptions}
                        selectedValues={selectedLabels}
                        style={multiSelectStyle}
                        onSelect={handleLabelSelect}
                        onRemove={handleRemove}
                        displayValue="label"
                        emptyRecordMsg={'No options available'}
                        selectionLimit={10}
                        avoidHighlightFirstOption
                    />
                    </div>
                </div>
                }
                {userPermission && (
                    <div className={styles.form_group}>
                        <label><strong><Trans>Group</Trans></strong></label>
                        <div className={styles.groupSelectContainer}>
                            {selectedGroup && (
                                <div className={styles.selectedGroup}>
                                    {selectedGroupObject?.group_name}
                                </div>
                            )}
                            <div className={styles.nonEditableField}>{selectedGroupObject?.group_name}</div>
                        </div>
                    </div>
                )}
                <div className={styles.form_row}>
                    <div className={`${styles.selectField} ${styles.group_select} ${styles.notes_area}`}>
                        <label><strong><Trans>Bucket</Trans></strong></label>
                        <div className={styles.nonEditableField}>{bucketName}</div>
                    </div>
                    <div className={styles.form_group}>
                        <label><strong><Trans>Progress</Trans></strong></label>
                        <div className={styles.nonEditableField}>{task?.progress}</div>
                    </div>
                    <div className={styles.form_group}>
                        <label><strong><Trans>Priority</Trans></strong></label>
                        <div className={styles.nonEditableField}>{task?.priority}</div>
                    </div>
                </div>
                <div className={styles.form_row}>
                    <div className={`${styles.dates_field} ${styles.form_group}`}>
                        <label><strong><Trans>Start Date</Trans></strong></label>
                        <div className={styles.nonEditableField}>{formatDate(task?.start_date ? new Date(task?.start_date) : null)}</div>
                    </div>
                    <div className={`${styles.dates_field} ${styles.form_group}`}>
                        <label><strong><Trans>Due Date</Trans></strong></label>
                        <div className={styles.nonEditableField}>{formatDate(task?.end_date ? new Date(task?.end_date) : null)}</div>
                    </div>
                    <div className={styles.form_group}>
                        <label><strong><Trans>Repeat</Trans></strong></label>
                        <div className={styles.nonEditableField}>{task?.repeat}</div>
                    </div>
                </div>
                <div className={styles.form_group}>
                    <label><strong><Trans>Notes</Trans></strong></label>
                    <div className={styles.nonEditableField}>{task?.notes}</div>
                </div>
                <div className={styles.form_group}>
                    <label><strong><Trans>Description</Trans></strong></label>
                    <div className={styles.nonEditableField}>{task?.description}</div>
                </div>
                {/* <div className={styles.form_group}>
                        <div className={styles.nonEditableField}>{task?.attachment}</div>
                </div> */}
                <div className={styles.form_group}>
                    <label><strong><Trans>Comments</Trans></strong></label>
                    <div className={styles.nonEditableField}>{task?.comments}</div>
                </div>
                <div className={styles.form_group}>
                    <label><strong><Trans>Added By</Trans></strong></label>
                    <div>{task?.added_by?.full_name}</div>
                    <div>{formatDateTime(task?.created_at)}</div>
                </div>
                {userPermission && task?.allow_update && (
                    <div className={styles.form_group}>
                        <label>Allow Update</label>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="allowUpdateCheckbox"
                                checked={task?.allow_update}
                                readOnly
                            />
                        </div>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
            </ModalFooter>
        </Modal>
    );
}

export default TeamProgressModal;
